import { createInterfaceId, Implements, Service, ServiceLifetime } from '@gitlab/needle';
import { Logger, withPrefix } from '@gitlab-org/logging';
import { ConfigService } from '@gitlab-org/config';
import {
  RepositoryDiscoveryService,
  tryParseRepositoryGitLabRemoteDetails,
} from '@gitlab-org/repositories';
import { URI } from 'vscode-uri';
import { RequestError } from '@agentclientprotocol/sdk';
import { SessionManager } from '../../sessions';
import type { Session } from '../../sessions/session';
import { SlashCommandService } from '../../slash_commands';

/**
 * Per-ACP-session bookkeeping needed to translate the CLI's `Session`
 * (accumulated-content messages) into ACP's incremental `session/update`
 * notifications, and to track turn-level state (cancellation, concurrency).
 */
export interface AcpSessionState {
  cliSession: Session;
  /** Length of `Message.content` already sent per assistant messageId, so we emit deltas, not the whole accumulated text each time. */
  sentTextLength: Map<string, number>;
  /**
   * Tool call ids we've already announced via a `tool_call` update (further
   * updates use `tool_call_update`). Deliberately NOT reset between turns: tool
   * ids are backend `message_id`s, unique per invocation within a session (see
   * `#stableToolId` in workflow_event_mapper.ts — the TUI relies on the same
   * session-lifetime uniqueness for element dedupe). Resetting per turn would
   * regress the case where a tool left pending by a cancelled turn settles in
   * the next turn's stream: the client already knows that id and expects a
   * `tool_call_update`, not a re-announcement.
   */
  announcedToolCallIds: Set<string>;
  /** Set to true when session/cancel fires during an active prompt turn. */
  cancelledDuringTurn: boolean;
  /** True while a session/prompt turn is being processed for this session. */
  turnInFlight: boolean;
}

export interface AcpSessionService {
  /**
   * Applies the client-provided cwd, creates a fresh CLI session, and
   * registers its state keyed by the session's own id.
   */
  createSession(cwd: string): Promise<{ sessionId: string }>;

  /** Looks up session state, throwing a JSON-RPC error for an unknown id. */
  requireState(sessionId: string): AcpSessionState;

  /** Marks a session cancelled and aborts its in-flight stream, if any. Unknown ids are ignored. */
  cancel(sessionId: string): void;
}

export const AcpSessionService = createInterfaceId<AcpSessionService>('AcpSessionService');

/**
 * Updates the workspace root in ConfigService when the ACP client provides a
 * `cwd` on session/new. Mirrors the logic in
 * CliInitializationService#setupWorkspaceAndConfig so the backend (project
 * detection, file context, sandbox) sees the correct root for this session.
 *
 * This mutates the process-wide singleton `ConfigService` (cwd,
 * workspaceFolders, projectPath), so it is only safe under the
 * single-active-session model this service enforces: SessionManager disposes
 * the previous backend on each createSession, so only one ACP session is ever
 * live and there is no concurrent second session whose cwd could be corrupted.
 * Per-session config isolation would be required for true multi-session
 * support.
 *
 * Exported standalone so tests can exercise it against real
 * `ConfigService`/`RepositoryDiscoveryService` instances and a real git repo.
 */
export function createSetCwd(
  configService: ConfigService,
  repositoryDiscoveryService: RepositoryDiscoveryService,
  logger: Logger,
): (cwd: string) => Promise<void> {
  return async (cwd: string): Promise<void> => {
    configService.set('cwd', URI.file(cwd).toString());
    let workspaceFolder: string;
    try {
      // findRepositoryRoot resolves to undefined when cwd is not inside a repo;
      // fall back to cwd, mirroring CliInitializationService#setupWorkspaceAndConfig.
      workspaceFolder = (await repositoryDiscoveryService.findRepositoryRoot(cwd)) ?? cwd;
    } catch {
      workspaceFolder = cwd;
    }
    configService.set('workspaceFolders', [
      { uri: URI.file(workspaceFolder).toString(), name: 'repository root' },
    ]);

    // Re-run GitLab project detection against the client-provided cwd:
    // CliInitializationService only detects against the process's startup
    // directory, which can differ from the workspace the client serves. This
    // keeps projectPath (workflow project attribution) accurate. Detection
    // yielding nothing leaves the startup value in place, matching
    // initialization's set-only-when-found behaviour. Known limit:
    // rootNamespaceId is not refreshed, so a cwd from a different top-level
    // namespace keeps the startup namespace.
    try {
      const repo = await repositoryDiscoveryService.getMatchingRepository(cwd, workspaceFolder);
      const baseUrl = configService.get('baseUrl');
      const remote =
        repo && baseUrl ? await tryParseRepositoryGitLabRemoteDetails(repo, baseUrl) : undefined;
      if (remote?.namespaceWithPath) {
        configService.set('projectPath', remote.namespaceWithPath);
      }
    } catch (error) {
      logger.warn(
        `project detection for ${cwd} failed`,
        error instanceof Error ? error : undefined,
      );
    }
  };
}

@Implements(AcpSessionService)
@Service({
  dependencies: [
    SessionManager,
    ConfigService,
    RepositoryDiscoveryService,
    Logger,
    SlashCommandService,
  ],
  lifetime: ServiceLifetime.Singleton,
})
export class DefaultAcpSessionService implements AcpSessionService {
  #sessionManager: SessionManager;

  #setCwd: (cwd: string) => Promise<void>;

  #logger: Logger;

  #slashCommandService: SlashCommandService;

  #states = new Map<string, AcpSessionState>();

  constructor(
    sessionManager: SessionManager,
    configService: ConfigService,
    repositoryDiscoveryService: RepositoryDiscoveryService,
    logger: Logger,
    slashCommandService: SlashCommandService,
  ) {
    this.#sessionManager = sessionManager;
    this.#logger = withPrefix(logger, '[AcpSessionService]');
    this.#setCwd = createSetCwd(configService, repositoryDiscoveryService, this.#logger);
    this.#slashCommandService = slashCommandService;
  }

  async createSession(cwd: string): Promise<{ sessionId: string }> {
    await this.#setCwd(cwd);
    // Custom commands/skills are project-specific, so re-registering per
    // session picks up the commands for this session's cwd rather than
    // whatever an earlier session (or process startup) last saw.
    this.#slashCommandService.removeDynamicCommands();
    const { warnings, conflicts } = await this.#slashCommandService.registerDynamicProviders();
    warnings.forEach((warning) => this.#logger.warn(warning));
    conflicts.forEach((conflict) => {
      const losers = conflict.losers
        .map((loser) => `"${loser.displayPath}" (${loser.source})`)
        .join(', ');
      this.#logger.warn(
        `Skill conflict "${conflict.skillName}": using "${conflict.winner.displayPath}" (${conflict.winner.source}); skipped ${losers}`,
      );
    });

    const { session } = await this.#sessionManager.createSession(undefined, {
      skipHistoryRehydration: true,
    });
    // Single-active-session by design: SessionManager disposes the previous
    // backend on every createSession, so any earlier ACP session is already
    // unusable. Evict prior #states entries here (rather than growing the map
    // unboundedly for a long-running server) so a prompt against a superseded
    // session fails loudly via requireState instead of running against a
    // disposed backend. True multi-session support would be a larger change.
    this.#states.clear();
    this.#states.set(session.sessionId, {
      cliSession: session,
      sentTextLength: new Map(),
      announcedToolCallIds: new Set(),
      cancelledDuringTurn: false,
      turnInFlight: false,
    });
    return { sessionId: session.sessionId };
  }

  requireState(sessionId: string): AcpSessionState {
    const state = this.#states.get(sessionId);
    if (!state) {
      throw RequestError.invalidParams(undefined, `Unknown or stale ACP session: ${sessionId}`);
    }
    return state;
  }

  cancel(sessionId: string): void {
    const state = this.#states.get(sessionId);
    if (!state) {
      this.#logger.debug(`session/cancel for unknown session: ${sessionId}`);
      return;
    }
    state.cancelledDuringTurn = true;
    state.cliSession.cancelStream();
  }
}
